import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), { signal });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      content.play();
    }
    
    // Start background audio if available and autoplay is enabled
    if (this.hasAttribute('autoplay')) {
      const audioElement = this.querySelector('audio.video-background-audio');
      if (audioElement && !audioElement.muted) {
        setTimeout(() => {
          audioElement.play().catch(() => {
            // Autoplay was prevented
          });
        }, 500);
      }
    }
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    if (this.isPlaying) {
      this.pauseMedia();
    } else {
      this.playMedia();
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      this.querySelector('video')?.play();
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
    
    // Play background audio if available
    const audioElement = this.querySelector('audio.video-background-audio');
    if (audioElement && !audioElement.muted) {
      audioElement.play().catch(() => {
        // Autoplay was prevented
      });
    }
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
    
    // Pause background audio if available
    const audioElement = this.querySelector('audio.video-background-audio');
    if (audioElement) {
      audioElement.pause();
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}

// Audio sync functionality for videos with background music
document.addEventListener('DOMContentLoaded', () => {
  const initializeVideoAudio = (element) => {
    const audioElement = element.querySelector('audio.video-background-audio');
    const muteButton = element.refs?.muteButton || element.querySelector('.video-mute-button');
    const audioUrl = element.getAttribute('data-audio-url');
    
    if (!muteButton) return;
    
    // If there's no background audio but a mute button exists, handle video muting
    if (!audioElement || !audioUrl) {
      const video = element.querySelector('video');
      
      // Load muted state from localStorage for video
      const elementId = element.id || `video-${Math.random().toString(36).substr(2, 9)}`;
      if (!element.id) {
        element.id = elementId;
      }
      const storedMuted = localStorage.getItem(`video-muted-${elementId}`);
      const isMuted = storedMuted === null ? true : storedMuted === 'true';
      
      // Set initial muted state
      if (video) {
        video.muted = isMuted;
      }
      
      const updateMuteButton = () => {
        const isVideoMuted = video ? video.muted : true;
        const unmutedIcon = muteButton.querySelector('.video-mute-button__icon--unmuted');
        const mutedIcon = muteButton.querySelector('.video-mute-button__icon--muted');
        
        if (unmutedIcon) unmutedIcon.classList.toggle('hidden', isVideoMuted);
        if (mutedIcon) mutedIcon.classList.toggle('hidden', !isVideoMuted);
        muteButton.setAttribute('aria-label', isVideoMuted ? 'Unmute' : 'Mute');
        muteButton.setAttribute('data-muted', String(isVideoMuted));
      };
      
      const handleMuteClick = () => {
        const currentVideo = element.querySelector('video');
        if (currentVideo) {
          currentVideo.muted = !currentVideo.muted;
          localStorage.setItem(`video-muted-${elementId}`, String(currentVideo.muted));
          updateMuteButton();
        }
      };
      
      muteButton.addEventListener('click', handleMuteClick);
      
      // Watch for video element when it loads from template
      if (!video) {
        const observer = new MutationObserver(() => {
          const loadedVideo = element.querySelector('video');
          if (loadedVideo) {
            loadedVideo.muted = isMuted;
            updateMuteButton();
            // Re-attach click handler to ensure it works after video loads
            muteButton.removeEventListener('click', handleMuteClick);
            muteButton.addEventListener('click', handleMuteClick);
            observer.disconnect();
          }
        });
        observer.observe(element, { childList: true, subtree: true });
        
        // Also check when data-media-loaded attribute is set
        const mediaObserver = new MutationObserver(() => {
          if (element.hasAttribute('data-media-loaded')) {
            const loadedVideo = element.querySelector('video');
            if (loadedVideo) {
              loadedVideo.muted = isMuted;
              updateMuteButton();
            }
          }
        });
        mediaObserver.observe(element, { attributes: true, attributeFilter: ['data-media-loaded'] });
      } else {
        updateMuteButton();
      }
      
      return;
    }
    
    // Handle background audio case
    
    // Load muted state from localStorage, default to muted (true) for new videos
    const elementId = element.id || `video-${Math.random().toString(36).substr(2, 9)}`;
    if (!element.id) {
      element.id = elementId;
    }
    const storedMuted = localStorage.getItem(`video-audio-muted-${elementId}`);
    // Default to muted (true) if no stored preference exists
    const isMuted = storedMuted === null ? true : storedMuted === 'true';
    audioElement.muted = isMuted;
    
    // Setup mute button
    if (muteButton) {
      const updateMuteButton = () => {
        const unmutedIcon = muteButton.querySelector('.video-mute-button__icon--unmuted');
        const mutedIcon = muteButton.querySelector('.video-mute-button__icon--muted');
        if (unmutedIcon) unmutedIcon.classList.toggle('hidden', audioElement.muted);
        if (mutedIcon) mutedIcon.classList.toggle('hidden', !audioElement.muted);
        muteButton.setAttribute('aria-label', audioElement.muted ? 'Unmute' : 'Mute');
        muteButton.setAttribute('data-muted', String(audioElement.muted));
      };
      
      muteButton.addEventListener('click', () => {
        audioElement.muted = !audioElement.muted;
        const video = element.querySelector('video');
        if (video) video.muted = audioElement.muted;
        localStorage.setItem(`video-audio-muted-${elementId}`, String(audioElement.muted));
        updateMuteButton();
      });
      
      updateMuteButton();
    }
    
    // Sync audio with video
    const video = element.querySelector('video');
    if (video) {
      video.addEventListener('play', () => {
        if (!audioElement.muted) {
          audioElement.play().catch(() => {
            // Autoplay was prevented
          });
        }
      });
      
      video.addEventListener('pause', () => {
        audioElement.pause();
      });
      
      video.addEventListener('ended', () => {
        audioElement.pause();
        audioElement.currentTime = 0;
      });
    }
    
    // For iframe videos, start audio when media loads
    const observer = new MutationObserver(() => {
      if (element.hasAttribute('data-media-loaded') && !audioElement.muted) {
        setTimeout(() => {
          audioElement.play().catch(() => {});
        }, 500);
      }
    });
    observer.observe(element, { attributes: true, attributeFilter: ['data-media-loaded'] });
    
    // Start audio if autoplay and not muted
    if (element.hasAttribute('autoplay') && !isMuted) {
      setTimeout(() => {
        audioElement.play().catch(() => {});
      }, 500);
    }
  };

  // Initialize existing elements - both with audio URL and with mute button only
  const deferredMediaElements = document.querySelectorAll('deferred-media[data-audio-url], deferred-media[data-show-mute-button="true"]');
  for (const element of deferredMediaElements) {
    initializeVideoAudio(element);
  }

  // Watch for new elements added dynamically
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType === 1) {
          if (node.matches && (node.matches('deferred-media[data-audio-url]') || node.matches('deferred-media[data-show-mute-button="true"]'))) {
            initializeVideoAudio(node);
          }
          const elements = node.querySelectorAll?.('deferred-media[data-audio-url], deferred-media[data-show-mute-button="true"]');
          if (elements) {
            for (const element of elements) {
              initializeVideoAudio(element);
            }
          }
        }
      }
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
});
